import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { ActionSchema } from '../../../utils/validation'
import PropTypes from 'prop-types'
import { CaretDownFill, CaretUpFill } from 'react-bootstrap-icons'

const Actions = ({ healthSystemId, actions, createAction, addActionState, updateActionOrder, updateAction, updateActionState, deleteAction, deleteActionState, activeActionSort, isDesc, sortActions, reset }) => {
  const [actionId, setActionId] = useState(-1)
  const [alertState, setAlertState] = useState({
    show: false,
    message: '',
    variant: 'info'
  })

  const addAction = useFormik({
    initialValues: {
      narrative: '',
      description: '',
      actionType: -1 
    },
    enableReinitialize: true,
    validationSchema: ActionSchema,
    onSubmit: (values) => {
      createAction({
        ...values,
        healthSystemId
      })
    }
  })

  const handleUpdateAction = (action) => {
    setActionId(action.actionId)
    if (!action.orderKey) {
      setAlertState({
        show: true,
        message: `Please update Order Key for ${action.name}`,
        variant: 'danger'
      })
    } else {
      updateAction(action, healthSystemId)
    }
  }

  const handleDeleteAction = (actionId) => {
    setActionId(actionId)
    deleteAction(actionId)
  }

  useEffect(() => {
    if (updateActionState.success) {
      setAlertState({
        show: true,
        message: 'Updated order.',
        variant: 'success'
      })
    }

    if (updateActionState.error !== false) {
      setAlertState({
        show: true,
        message: updateActionState.error.message,
        variant: 'danger'
      })
    }
  }, [updateActionState.success, updateActionState.error])

  useEffect(() => {
    if (deleteActionState.success) {
      setAlertState({
        show: true,
        message: 'Removed order.',
        variant: 'success'
      })
    }

    if (deleteActionState.error !== false) {
      setAlertState({
        show: true,
        message: deleteActionState.error.message,
        variant: 'danger'
      })
    }
  }, [deleteActionState.success, deleteActionState.error])

  useEffect(() => {
    setAlertState({
      show: false,
      variant: 'info',
      message: ''
    })
    return () => reset()
  }, [])

  useEffect(() => {
    if (addActionState.success) {
      setAlertState({
        show: true,
        message: 'Added new order.',
        variant: 'success'
      })
    }

    if (addActionState.error !== false) {
      setAlertState({
        show: true,
        message: addActionState.error.message,
        variant: 'danger'
      })
    }
  }, [addActionState.success, addActionState.error])

  return (
    <Container fluid className='mx-3'>
      <Row>
        <Col xs={12}>
          <Form id='form' noValidate onSubmit={addAction.handleSubmit}>
            <Row>
              <Form.Group as={Col} xs={3}>
                <Form.Label>Narrative</Form.Label>
                <Form.Control
                  id='narrative'
                  name='narrative'
                  size='sm'
                  value={addAction.values.narrative}
                  onChange={addAction.handleChange}
                  onBlur={addAction.handleBlur}
                  isInvalid={!!addAction.errors.narrative && addAction.touched.narrative}
                />
                <Form.Control.Feedback type='invalid'>
                  {addAction.errors.narrative}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} xs={3}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  id='description'
                  name='description'
                  size='sm'
                  value={addAction.values.description}
                  onChange={addAction.handleChange}
                  onBlur={addAction.handleBlur}
                  isInvalid={!!addAction.errors.description && addAction.touched.description}
                />
                <Form.Control.Feedback type='invalid'>
                  {addAction.errors.description}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} xs={2}>
                <Form.Label>Action Type</Form.Label>
                <Form.Control 
                  id='actionType'
                  name='actionType'
                  size='sm'
                  custom
                  as='select'
                  value={addAction.values.actionType}
                  onChange={addAction.handleChange}
                  onBlur={addAction.handleBlur}
                  isInvalid={!!addAction.errors.actionType && addAction.touched.actionType}
                >
                  <option value={-1} hidden>Select Action Type</option>
                  <option value={1}>Order</option>
                  <option value={2}>Referral</option>
                  <option value={3}>Med Order</option>
                  <option value={5}>Referral Order</option>
                </Form.Control>
                <Form.Control.Feedback type='invalid'>
                  {addAction.errors.actionType}
                </Form.Control.Feedback>
              </Form.Group>
              <Col xs={2}>
                <Button style={{ marginTop: '27px' }} type='submit' disabled={addActionState.loading}>
                  {
                    addActionState.loading 
                      ? <Spinner animation='border' role='status' size='sm' />
                      : '+ Add New Action'
                  }
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
        <Col xs={8}>
          <Alert
            show={alertState.show} 
            variant={alertState.variant} 
            dismissible 
            onClose={() => setAlertState((ps) => ({ ...ps, show: false }))}
            className='font-85'
          >
            {alertState.message}  
          </Alert>
        </Col>
      </Row>
      <Row className='table-header mt-2'>
        <Col xs={3}>
          <span className='table-header-cell' onClick={() => sortActions('name')}>
            Narrative
            {
              activeActionSort === 'name' 
                ? isDesc 
                  ? <CaretDownFill className='ml-2' /> 
                  : <CaretUpFill className='ml-2' /> 
                : ''
            }
          </span>
        </Col>
        <Col xs={2} className='table-header-cell'>
          <span className='table-header-cell' onClick={() => sortActions('orderKey')}>
            Order Key
            {
              activeActionSort === 'orderKey' 
                ? isDesc 
                  ? <CaretDownFill className='ml-2' /> 
                  : <CaretUpFill className='ml-2' /> 
                : ''
            }
          </span>
        </Col>
        <Col xs={3} className='table-header-cell'>
          <span className='table-header-cell' onClick={() => sortActions('description')}>
            Description
            {
              activeActionSort === 'description' 
                ? isDesc 
                  ? <CaretDownFill className='ml-2' /> 
                  : <CaretUpFill className='ml-2' /> 
                : ''
            }
          </span>
        </Col>
        <Col xs={1} className='text-center'>
          Action Type
        </Col>
      </Row>
      {
        actions.map((action) => (
          <Row
            key={`action-${action.actionId}`}
            className='table-body mt-2 mb-1'
          >
            <Col xs={3}>
              {
                action.isPreset !== 1
                  ? (
                    <Form.Control
                      size='sm'
                      id='name'
                      value={action.name}
                      onChange={(event) => updateActionOrder(action.actionId, 'name', event.target.value)}
                    />
                  ) : action.name
              }
            </Col>
            <Col xs={2}>
              <Form.Control
                size='sm'
                id='orderKey'
                name='orderKey'
                value={action.orderKey}
                onChange={(event) => updateActionOrder(action.actionId, 'orderKey', event.target.value)}
              />
            </Col>
            <Col xs={3}>
              <Form.Control
                size='sm'
                id='description'
                name='description'
                value={action.description}
                onChange={(event) => updateActionOrder(action.actionId, 'description', event.target.value)}
              />
            </Col>
            <Col xs={2}>
              <Form.Control 
                id='actionTypeId'
                name='actionTypeId'
                size='sm'
                custom
                as='select'
                value={parseInt(action.actionTypeId)}
                onChange={(event) => updateActionOrder(action.actionId, 'actionTypeId', event.target.value)}
              >
                <option value={-1} hidden>Select Action Type</option>
                <option value={1}>Order</option>
                <option value={2}>Referral</option>
                <option value={3}>Med Order</option>
                <option value={4}>Patient Ed</option>
                <option value={5}>Referral Order</option>
              </Form.Control>
            </Col>
            <Col xs={2}>
              <Button 
                className='mr-2' 
                size='sm' 
                variant='outline-primary'
                disabled={actionId === action.actionId && updateActionState.loading}
                onClick={() => handleUpdateAction(action)}
              >
                Update
              </Button>
              {
                action.isPreset !== 1
                  ? (
                    <Button 
                      size='sm' 
                      variant='outline-danger'
                      disabled={actionId === action.actionId && deleteActionState.loading}
                      onClick={() => handleDeleteAction(action.actionId)}
                    >
                    X
                    </Button>
                  ) : null
              }
              
            </Col>
          </Row>
        ))
      }
    </Container>
  )
}

Actions.propTypes = {
  healthSystemId: PropTypes.number,
  actions: PropTypes.array,
  user:  PropTypes.shape({
    userId: PropTypes.number,
    email: PropTypes.string,
    status: PropTypes.string,
    roleId: PropTypes.number
  }),
  createAction: PropTypes.func,
  addActionState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
  updateActionOrder: PropTypes.func,
  updateAction: PropTypes.func,
  updateActionState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
  deleteAction: PropTypes.func,
  deleteActionState: PropTypes.shape({
    loading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string
      })
    ])
  }),
  activeActionSort: PropTypes.string,
  isDesc: PropTypes.bool,
  sortActions: PropTypes.func,
  reset: PropTypes.func
}

export default Actions